/**
 * The two server calls this feature makes: who can be assigned to, and the assignment itself.
 *
 * ── The roster read is this slice's own, and does NOT reuse the clients page's ───────────────
 * `pages/clients` reads the same endpoint with `includeMetrics=true` and is a frozen spike nothing
 * may build on (`pages/clients/AGENTS.md`), so sharing its `queryOptions` would be exactly the
 * promotion out of it that CLAUDE.md forbids. The parameters differ anyway: a picker needs a name,
 * an e-mail and a status, and the seven metric keys would only make the response bigger. Different
 * parameters, different key, different cache entry.
 *
 * ── `lang` on the write, and no follow-up read ───────────────────────────────────────────────
 * `PlanAssignmentController.Assign` takes `lang` and finishes with `GetByIdAsync(id, language)`, so
 * the POST's own response is the localised assignment — the same trick `savePlanTemplate` relies on.
 * A blank `lang` resolves to English rather than erroring, so omitting it is silently wrong: the
 * confirmation would name the plan's exercises in the wrong language.
 *
 * ── The invalidation lives in `lib/useAssignFlow`, not here ──────────────────────────────────
 * An assignment changes the client's `planName` on the roster and creates a whole schedule, and two
 * queries publish those: the customer list the roster's AKTYWNY PLAN column reads, and the client's
 * assignment list the calendar's plan picker draws. Both are invalidated on success, where the
 * query client is.
 *
 * For most of this feature's life neither was, and the note here said why: the app-wide
 * `staleTime: 30_000` held the gap shut because no navigation crossed the window — this dialog is
 * opened from the builder, and the calendar is reached from the client detail. That stopped being
 * true on 18 August 2026, when the confirmation's last line became "Postępy klienta zobaczysz w
 * jego kalendarzu, w karcie Klienci". A trainer who follows an instruction the app just gave them
 * is inside the window by definition.
 */

import { queryOptions } from '@tanstack/react-query';

import { getCustomer } from '~/shared/api/generated/endpoints/customer/customer';
import { postPlanAssignment } from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import type {
  TrainWithMeModulesTrainingDtosRequestsPostPlanAssignmentRequest as PostPlanAssignmentRequest,
  TrainWithMeModulesTrainingDtosResponsesPlanAssignmentFullResponse as PlanAssignmentFullResponse,
} from '~/shared/api/generated/model';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * The roster, as assignment targets.
 *
 * Not keyed on `lang`: nothing in this response is server-localised — names, e-mails and a status
 * token are all data — so a language switch must not refetch it or split the cache in two.
 */
export function assignableClientsQueryOptions() {
  return queryOptions({
    queryKey: ['assign-plan', 'clients'] as const,
    queryFn: ({ signal }) => getCustomer(undefined, undefined, signal),
  });
}

/** Assign a library template. The response is the created assignment, fully localised. */
export function assignPlan(
  body: PostPlanAssignmentRequest,
  lang: ContentLanguage,
): Promise<PlanAssignmentFullResponse> {
  return postPlanAssignment(body, { lang });
}
